import React, { useState, useEffect } from 'react';
import { Menu, Input, Button, Drawer } from 'antd';
import { eventBus } from '@/utils/eventBus';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import type { GetProp, MenuProps } from 'antd';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setYourTownloopSearchValue } from '@/store/slices/searchBarSlice';
import styles from './index.module.scss';
import { MenuOutlined } from '@ant-design/icons';

const { Search } = Input;

type MenuItem = GetProp<MenuProps, 'items'>[number];

const YourTownloop: React.FC = () => {

  //国际化语言切换
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  //是否是手机端
  const isMobile = useAppSelector((state) => state.searchBar.isMobile);

  //yourTownloopSearchValue关键字
  const yourTownloopSearchValue = useAppSelector((state) => state.searchBar.yourTownloopSearchValue);

  //组件内 Search 搜索框值
  const [townloopSearchValue, setTownloopSearchValue] = useState<string>('');

  //路由跳转对象
  const navigate = useNavigate();

  //获取当前路径
  const location = useLocation();

  // 获取查询参数
  const [searchParams] = useSearchParams();

  const [isSeller, setIsSeller] = useState(false);


  //根据当前路径获取选中的菜单项
  const getSelectedKeys = () => {
    const pathname = location.pathname;
    const pathToKeyMap: { [key: string]: string } = {
      '/yourtownloop': 'home',
      '/yourtownloop/cart': 'cart',
      '/yourtownloop/orders': 'productOrders',
      '/yourtownloop/postorders': 'postOrders',
      '/yourtownloop/serviceorders': 'serviceOrders',
      '/yourtownloop/favorites': 'favorites',
      '/yourtownloop/followedStores': 'followedStores',
      '/yourtownloop/yourPaymentMethods': 'payment',
      '/yourtownloop/refundsSupport': 'refunds',
      '/yourtownloop/browsingHistory': 'history',
      '/yourtownloop/accountSecurity': 'security',
      '/yourtownloop/yourProfile': 'profile',
      '/yourtownloop/addresses': 'addresses',
      '/yourtownloop/engineer_zone': 'engineer_zone',
      '/yourtownloop/engineer_information': 'engineer_information',
      '/yourtownloop/yourStore': 'yourStore',
      '/yourtownloop/storeInformation': 'storeInformation',
      '/yourtownloop/yourProducts': 'yourProducts',
      '/yourtownloop/storeOrders': 'storeOrders',
      '/yourtownloop/createLiveStream': 'liveNow',
      '/yourtownloop/liveStreamingProduct': 'liveStreamingProduct',
      '/yourtownloop/liveList': 'LiveList',
    };

    return [pathToKeyMap[pathname] || 'home'];
  };

  //获取用户信息
  const getUserInfo = () => {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        return JSON.parse(userInfoStr);
      }
    } catch (error) {
      console.error('解析userInfo失败:', error);
    }
    return null;
  };

  //检查是否为商家
  const isMerchant = () => {
    const userInfo = getUserInfo();
    return userInfo && userInfo.merchant_id !== null;
  };

  //主菜单数据
  const menuItems: MenuItem[] = [
    ...((!isSeller) ? [
      {
        key: 'cart',
        label: t('YourTownLoop.Cart'),
        onClick: () => navigate('/yourtownloop/cart')
      },
      {
        key: 'orders',
        label: t('YourTownLoop.YourOrders'),
        children: [
          {
            key: 'productOrders',
            label: t('YourTownLoop.ProductOrders'),
            onClick: () => navigate('/yourtownloop/orders')
          },
          {
            key: 'serviceOrders',
            label: t('YourTownLoop.ServiceOrders'),
            onClick: () => navigate('/yourtownloop/serviceorders')
          },
          {
            key: 'postOrders',
            label: t('YourTownLoop.PostedOrders'),
            onClick: () => navigate('/yourtownloop/postorders')
          }
        ],
      },
      // { key: 'coupons', label: 'Coupons & Offers' },
      {
        key: 'favorites',
        label: t('YourTownLoop.YourFavorites'),
        onClick: () => navigate('/yourtownloop/favorites')
      },
      { 
        key: 'followedStores', 
        label: t('YourTownLoop.FollowedStores'),
        onClick: () => navigate('/yourtownloop/followedStores') 
      },
      // { 
      //   key: 'payment', 
      //   label: t('YourTownLoop.YourPaymentMethods'),
      //   onClick: () => navigate('/yourtownloop/yourPaymentMethods')  
      // },
      // { key: 'reviews', label: 'Your Reviews' },
      // { 
      //   key: 'refunds', 
      //   label: t('YourTownLoop.Refunds&Support'), 
      //   onClick: () => navigate('/yourtownloop/refundsSupport')   
      // },
      {
        key: 'history',
        label: t('YourTownLoop.BrowsingHistory'),
        onClick: () => navigate('/yourtownloop/browsingHistory')
      },
      {
        key: 'account',
        label: t('YourTownLoop.AccountSettings'),
        children: [
          {
            key: 'security',
            label: t('YourTownLoop.AccountSecurity'),
            onClick: () => navigate('/yourtownloop/accountSecurity')
          },
          {
            key: 'profile',
            label: t('YourTownLoop.YourProfile'),
            onClick: () => navigate('/yourtownloop/yourProfile')
          },
          {
            key: 'addresses',
            label: t('YourTownLoop.Addresses'),
            onClick: () => navigate('/yourtownloop/addresses')
          },
          {
            key: 'engineer_zone',
            label: t('YourTownLoop.EngineerZone'),
            onClick: () => navigate('/yourtownloop/engineer_zone')
          },
        ],
      },

      {
        key: 'liveManagement',
        label: t('YourTownLoop.LiveManagement'),
        children: [
          // { 
          //   key: 'liveNow', 
          //   label: t('YourTownLoop.livenow'), 
          //   onClick: () => navigate('/yourtownloop/goLiveNow')
          // },
          {
            key: 'liveNow',
            label: t('YourTownLoop.liveRoom'),
            onClick: () => navigate('/yourtownloop/createLiveStream')
          },
          {
            key: 'liveStreamingProduct',
            label: t('YourTownLoop.LiveStreamProducts'),
            onClick: () => navigate('/yourtownloop/liveStreamingProduct')
          },
          // { 
          //   key: 'LiveList', 
          //   label: t('YourTownLoop.LiveList')'', 
          //   onClick: () => navigate('/yourtownloop/liveList')
          // },
        ],
      }

    ] : []),



    // 只有商家才显示此菜单
    ...((isMerchant() && isSeller) ? [{
      key: 'storeManagement',
      label: t('YourTownLoop.StoreManagement'),
      children: [
        {
          key: 'yourStore',
          label: t('YourTownLoop.YourStore'),
          onClick: () => navigate('/yourtownloop/yourStore')
        },
        {
          key: 'storeInformation',
          label: t('YourTownLoop.StoreInformation'),
          onClick: () => navigate('/yourtownloop/storeInformation')
        },
        {
          key: 'yourProducts',
          label: t('YourTownLoop.ManageProducts'),
          onClick: () => navigate('/yourtownloop/yourProducts')
        },
        {
          key: 'storeOrders',
          label: t('YourTownLoop.ManageOrders'),
          onClick: () => navigate('/yourtownloop/storeOrders')
        },
      ],
    }] : []),
  ];
  //Search组件搜索事件
  const onSearchAction = (value: string) => {
    dispatch(setYourTownloopSearchValue(value));
  }

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTownloopSearchValue(e.target.value)
  };
  

  //mounted和unmounted
  useEffect(() => {


    const handler = () => {
      //debugger
      console.log('收到子页面卸载通知');
      setTownloopSearchValue('');
    };
    eventBus.on('deleteTownloopSearchValue', handler);

    return () => {
      eventBus.off('deleteTownloopSearchValue', handler); // 清理监听
    };
  }, []);


  useEffect(() => {

    if (location.pathname === '/yourtownloop') {
      const flag = searchParams.get('flag') || '1';

      if (flag === '1') {
        setIsSeller(false);
      } else if (flag === '2') {
        setIsSeller(true);

        navigate('/yourtownloop/yourStore');

      }
    }

  }, [searchParams]);

  useEffect(() => {
    // 定义商家相关的路由路径
    const sellerRoutes = [
      '/yourtownloop/yourStore',
      '/yourtownloop/storeInformation',
      '/yourtownloop/yourProducts',
      '/yourtownloop/storeOrders'
    ];

    // 检查当前路由是否是商家路由
    const isSellerRoute = sellerRoutes.some(route =>
      location.pathname.startsWith(route)
    );

    if (isSellerRoute) {
      setIsSeller(true);
    } else {
      // if( location.pathname !== '/yourtownloop' ){
      setIsSeller(false);
      // }
    }
  }, [location.pathname]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      {/* <div className={styles['search-box']}>
        <div className={styles['search-bar']}>
          <Search
            placeholder="table lamp"
            enterButton="Search"
            className={styles['search-input']}
            onSearch={onSearchAction}
            value={townloopSearchValue}
            onChange={onSearchInputChange}
          />
        </div>
      </div> */}
      <div className={styles['yourtownloop-layout']}>
        {
          !isMobile && (
            <div className={styles['sidebar']}>
              <div className={styles['title']} onClick={() => navigate('/yourtownloop')}>Your Townloop</div>
              <Menu
                mode="inline"
                items={menuItems}
                className={styles['menu']}
                defaultOpenKeys={['orders', 'account', 'storeManagement']}
                selectedKeys={getSelectedKeys()} />
            </div>
          )
        }

        <div className={styles['content']}>
          {
            isMobile &&
            (
              <div className={styles['sidebar-mobile']}>

                <Button
                  shape="circle"
                  size="large"
                  icon={<MenuOutlined />}
                  className={styles['sidebar-fab']}
                  onClick={() => setDrawerOpen(true)}
                />
                <Drawer
                  title="Your Townloop"
                  placement="left"
                  onClose={() => setDrawerOpen(false)}
                  open={drawerOpen}
                >
                  <Menu
                    mode="inline"
                    items={menuItems}
                    className={styles['menu']}
                    defaultOpenKeys={['orders', 'account']}
                    selectedKeys={getSelectedKeys()}
                    onClick={() => setDrawerOpen(false)}
                  />
                </Drawer>
              </div>
            )
          }
          <Outlet />
          {/* 这里可以放主内容 */}
        </div>
      </div>
    </>

  );
};

export default YourTownloop; 